import {
  useQuery,
  QueryKey,
  QueryObserverOptions,
  UseQueryResult,
  MutationKey,
  useMutation,
  MutateOptions,
} from "react-query";

import {
  departmentListService,
  departmentListShortService,
  departmentDetailService,
  departmentCreateService,
  departmentUpdateService,
  departmentDeleteService,
  departmentUpdateStatusService,
} from "./services";

type DepartmentListResponse = Awaited<ReturnType<typeof departmentListService>>;
type DepartmentShortListResponse = Awaited<
  ReturnType<typeof departmentListShortService>
>;
type DepartmentDetailResponse = Awaited<
  ReturnType<typeof departmentDetailService>
>;

export const useDepartments = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params: Parameters<typeof departmentListService>[0];
  options?: QueryObserverOptions<unknown, unknown, DepartmentListResponse>;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "departmentListHooks",
    queryFn: () => departmentListService(params),
    ...options,
  });

  return response as UseQueryResult<DepartmentListResponse>;
};

export const useShortDepartments = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params: Parameters<typeof departmentListShortService>[0];
  options?: QueryObserverOptions<unknown, unknown, DepartmentShortListResponse>;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "departmentShortListHooks",
    queryFn: () => departmentListShortService(params),
    ...options,
  });

  return response as UseQueryResult<DepartmentShortListResponse>;
};

export const useDepartmentDetail = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params: Parameters<typeof departmentDetailService>[0];
  options?: QueryObserverOptions<unknown, unknown, DepartmentDetailResponse>;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "departmentDetailHooks",
    queryFn: () => departmentDetailService(params),
    ...options,
  });

  return response as UseQueryResult<DepartmentDetailResponse>;
};

export const useDepartmentUpdate = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    DepartmentDetailResponse,
    unknown,
    Parameters<typeof departmentUpdateService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "departmentUpdateHooks",
    mutationFn: departmentUpdateService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};

export const useDepartmentCreate = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    DepartmentDetailResponse,
    unknown,
    Parameters<typeof departmentCreateService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "departmentCreateHooks",
    mutationFn: departmentCreateService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};

export const useDepartmentDelete = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    DepartmentDetailResponse,
    unknown,
    Parameters<typeof departmentDeleteService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "departmentDeleteService",
    mutationFn: departmentDeleteService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};

export const useDepartmentUpdateStatus = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    DepartmentDetailResponse,
    unknown,
    Parameters<typeof departmentUpdateStatusService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "departmentUpdateStatusService",
    mutationFn: departmentUpdateStatusService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};
